import React, { useEffect, useMemo, useState } from 'react';

const toNumber = (v) => {
  const n = Number(String(v ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const fmtUsd = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(toNumber(n));

const pad2 = (n) => String(n).padStart(2, '0');

const toYYYYMMDD = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const parseDayFromNoteRow = (r) => {
  // Prefer explicit "date" column (you already store this on notes sometimes)
  const dateStr = String(r?.date || r?.Date || '').trim();
  if (dateStr) {
    // If it already is YYYY-MM-DD, keep it
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;

    // Try parsing other formats
    const dt = new Date(dateStr);
    const ymd = toYYYYMMDD(dt);
    if (ymd) return ymd;
  }

  // Fall back to createdAt / created_at
  const createdAt = String(r?.createdAt || r?.created_at || '').trim();
  if (createdAt) {
    const dt = new Date(createdAt);
    const ymd = toYYYYMMDD(dt);
    if (ymd) return ymd;
  }

  return 'Unknown';
};

const parseMealCountNote = (note) => {
  // expected format: "Meal count — Breakfast: 120"
  const s = String(note || '').trim();
  const m = s.match(/Meal\s*count\s*—\s*(Breakfast|Lunch|Dinner)\s*:\s*(\d+)/i);
  if (!m) return null;

  const meal = m[1][0].toUpperCase() + m[1].slice(1).toLowerCase();
  const count = Number(m[2]);
  if (!Number.isFinite(count)) return null;

  return { meal, count };
};

const mealTableByDaySum = (notesRows) => {
  // date -> { Breakfast, Lunch, Dinner }
  const map = new Map();

  (notesRows || []).forEach((r) => {
    const note = String(r?.note || r?.Note || '').trim();
    const parsed = parseMealCountNote(note);
    if (!parsed) return;

    const day = parseDayFromNoteRow(r);
    if (!map.has(day)) {
      map.set(day, { Breakfast: 0, Lunch: 0, Dinner: 0 });
    }
    const rec = map.get(day);
    rec[parsed.meal] += parsed.count;
  });

  const days = Array.from(map.keys()).sort((a, b) => {
    // Put Unknown last
    if (a === 'Unknown') return 1;
    if (b === 'Unknown') return -1;
    return a.localeCompare(b);
  });

  const rows = days.map((day) => {
    const rec = map.get(day) || { Breakfast: 0, Lunch: 0, Dinner: 0 };
    return {
      day,
      Breakfast: rec.Breakfast || 0,
      Lunch: rec.Lunch || 0,
      Dinner: rec.Dinner || 0,
      Total: (rec.Breakfast || 0) + (rec.Lunch || 0) + (rec.Dinner || 0)
    };
  });

  // Grand totals
  const totals = rows.reduce(
    (acc, r) => {
      acc.Breakfast += r.Breakfast;
      acc.Lunch += r.Lunch;
      acc.Dinner += r.Dinner;
      acc.Total += r.Total;
      return acc;
    },
    { Breakfast: 0, Lunch: 0, Dinner: 0, Total: 0 }
  );

  return { rows, totals };
};

export default function ChairmanReports({ token }) {
  const [regRows, setRegRows] = useState([]);
  const [finRows, setFinRows] = useState([]);
  const [notesRows, setNotesRows] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchAll = async () => {
    setError('');
    setLoading(true);
    try {
      const [regsRes, finRes, notesRes] = await Promise.all([
        fetch('/.netlify/functions/admin-get-chairman-registrations', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch('/.netlify/functions/admin-get-finance', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch('/.netlify/functions/admin-get-supplies-notes', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      // regs
      {
        const raw = await regsRes.text();
        let json = {};
        try { json = raw ? JSON.parse(raw) : {}; } catch { json = { error: raw }; }
        if (!regsRes.ok) throw new Error(json?.error || `Registrations failed (${regsRes.status})`);
        setRegRows(Array.isArray(json.rows) ? json.rows : []);
      }

      // finance
      {
        const raw = await finRes.text();
        let json = {};
        try { json = raw ? JSON.parse(raw) : {}; } catch { json = { error: raw }; }
        if (!finRes.ok) throw new Error(json?.error || `Finance failed (${finRes.status})`);
        setFinRows(Array.isArray(json.rows) ? json.rows : []);
      }

      // notes
      {
        const raw = await notesRes.text();
        let json = {};
        try { json = raw ? JSON.parse(raw) : {}; } catch { json = { error: raw }; }
        if (!notesRes.ok) throw new Error(json?.error || `Notes failed (${notesRes.status})`);
        setNotesRows(Array.isArray(json.rows) ? json.rows : []);
      }
    } catch (e) {
      setError(e?.message || 'Failed to load report data');
      setRegRows([]);
      setFinRows([]);
      setNotesRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const report = useMemo(() => {
    const totalRegistered = regRows.length;
    const totalAssistance = regRows.filter((r) => String(r?.needsAssistance || '').toLowerCase() === 'yes').length;

    const totalIncome = finRows
      .filter((r) => String(r?.type || '').toLowerCase() === 'income')
      .reduce((sum, r) => sum + toNumber(r.amount), 0);

    const totalExpense = finRows
      .filter((r) => String(r?.type || '').toLowerCase() === 'expense')
      .reduce((sum, r) => sum + toNumber(r.amount), 0);

    const net = totalIncome - totalExpense;

    const mealTable = mealTableByDaySum(notesRows);

    // finance sort newest -> oldest
    const sortedFin = [...finRows].sort((a, b) => {
      const ad = Date.parse(a.date || a.Date || a.createdAt || a.created_at || '') || 0;
      const bd = Date.parse(b.date || b.Date || b.createdAt || b.created_at || '') || 0;
      return bd - ad;
    });

    return {
      totalRegistered,
      totalAssistance,
      totalIncome,
      totalExpense,
      net,
      mealTable,
      sortedFin
    };
  }, [regRows, finRows, notesRows]);

  const doPrint = () => {
    // wait for DOM to be ready
    requestAnimationFrame(() => {
      requestAnimationFrame(() => window.print());
    });
  };

  return (
    <div style={{ marginTop: 12, display: 'grid', gap: 12 }}>
      {/* Print area: off-screen but NOT display:none (so it prints) */}
      <div className="print-area" style={{ position: 'absolute', left: '-10000px', top: 0 }}>
        <h1>Camp Fusion Report</h1>
        <p><strong>Generated:</strong> {new Date().toLocaleString()}</p>

        <h2>Registrations (numbers only)</h2>
        <p><strong>Total registered:</strong> {report.totalRegistered}</p>
        <p><strong>Total requesting assistance:</strong> {report.totalAssistance}</p>

        <h2>Meal counts (by day, summed)</h2>
        {report.mealTable.rows.length ? (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Date', 'Breakfast', 'Lunch', 'Dinner', 'Total'].map((h) => (
                    <th key={h} style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: '6px 8px' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.mealTable.rows.map((r) => (
                  <tr key={r.day}>
                    <td style={{ borderBottom: '1px solid #eee', padding: '6px 8px' }}>{r.day}</td>
                    <td style={{ borderBottom: '1px solid #eee', padding: '6px 8px' }}>{r.Breakfast}</td>
                    <td style={{ borderBottom: '1px solid #eee', padding: '6px 8px' }}>{r.Lunch}</td>
                    <td style={{ borderBottom: '1px solid #eee', padding: '6px 8px' }}>{r.Dinner}</td>
                    <td style={{ borderBottom: '1px solid #eee', padding: '6px 8px' }}>{r.Total}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td style={{ padding: '6px 8px', fontWeight: 800 }}>Totals</td>
                  <td style={{ padding: '6px 8px', fontWeight: 800 }}>{report.mealTable.totals.Breakfast}</td>
                  <td style={{ padding: '6px 8px', fontWeight: 800 }}>{report.mealTable.totals.Lunch}</td>
                  <td style={{ padding: '6px 8px', fontWeight: 800 }}>{report.mealTable.totals.Dinner}</td>
                  <td style={{ padding: '6px 8px', fontWeight: 800 }}>{report.mealTable.totals.Total}</td>
                </tr>
              </tfoot>
            </table>
          </>
        ) : (
          <p>No meal count notes found.</p>
        )}

        <h2>Financial summary</h2>
        <p><strong>Total income:</strong> {fmtUsd(report.totalIncome)}</p>
        <p><strong>Total expenses:</strong> {fmtUsd(report.totalExpense)}</p>
        <p><strong>Net:</strong> {fmtUsd(report.net)}</p>

        <h2>Financial transactions (all)</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Date', 'Type', 'Category', 'Description', 'Amount'].map((h) => (
                <th key={h} style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: '6px 8px' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {report.sortedFin.map((r, idx) => (
              <tr key={String(r.id || r.ID || idx)}>
                <td style={{ borderBottom: '1px solid #eee', padding: '6px 8px' }}>
                  {String(r.date || r.Date || r.createdAt || r.created_at || '').trim()}
                </td>
                <td style={{ borderBottom: '1px solid #eee', padding: '6px 8px' }}>
                  {String(r.type || '').trim()}
                </td>
                <td style={{ borderBottom: '1px solid #eee', padding: '6px 8px' }}>
                  {String(r.category || r.Category || '').trim()}
                </td>
                <td style={{ borderBottom: '1px solid #eee', padding: '6px 8px', whiteSpace: 'pre-wrap' }}>
                  {String(r.description || r.Description || r.notes || r.Notes || '').trim()}
                </td>
                <td style={{ borderBottom: '1px solid #eee', padding: '6px 8px' }}>
                  {fmtUsd(r.amount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'baseline' }}>
          <div>
            <h2 style={{ margin: 0 }}>Reports</h2>
            <div className="muted" style={{ marginTop: 4 }}>
              Print registrations/assistance counts + meal counts by day + full finance transaction list.
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn" onClick={fetchAll} disabled={loading}>
              Refresh
            </button>
            <button className="btn primary" onClick={doPrint} disabled={loading || !!error}>
              Print report
            </button>
          </div>
        </div>

        {error ? <div className="error" style={{ marginTop: 10 }}>{error}</div> : null}
        {loading ? <div style={{ marginTop: 10 }}>Loading…</div> : null}

        {!loading && !error ? (
          <div style={{ marginTop: 12, display: 'grid', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
              <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 12, background: '#fff' }}>
                <div className="muted" style={{ fontSize: 12 }}>Registered</div>
                <div style={{ fontSize: 28, fontWeight: 900, marginTop: 6 }}>{report.totalRegistered}</div>
              </div>
              <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 12, background: '#fff' }}>
                <div className="muted" style={{ fontSize: 12 }}>Needs assistance</div>
                <div style={{ fontSize: 28, fontWeight: 900, marginTop: 6 }}>{report.totalAssistance}</div>
              </div>
              <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 12, background: '#fff' }}>
                <div className="muted" style={{ fontSize: 12 }}>Net</div>
                <div style={{ fontSize: 20, fontWeight: 900, marginTop: 6 }}>{fmtUsd(report.net)}</div>
              </div>
            </div>

            <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 12, background: '#fff' }}>
              <div style={{ fontWeight: 900, marginBottom: 8 }}>Meal counts by day (summed)</div>

              {report.mealTable.rows.length ? (
                <div className="table-wrap">
                  <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                    <thead>
                      <tr>
                        {['Date', 'Breakfast', 'Lunch', 'Dinner', 'Total'].map((h) => (
                          <th key={h} style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #eee', fontSize: 12, color: '#666' }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {report.mealTable.rows.map((r) => (
                        <tr key={r.day}>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f1f1' }}>{r.day}</td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f1f1' }}>{r.Breakfast}</td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f1f1' }}>{r.Lunch}</td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f1f1' }}>{r.Dinner}</td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f1f1' }}>{r.Total}</td>
                        </tr>
                      ))}
                      <tr>
                        <td style={{ padding: '6px 8px', fontWeight: 900 }}>Totals</td>
                        <td style={{ padding: '6px 8px', fontWeight: 900 }}>{report.mealTable.totals.Breakfast}</td>
                        <td style={{ padding: '6px 8px', fontWeight: 900 }}>{report.mealTable.totals.Lunch}</td>
                        <td style={{ padding: '6px 8px', fontWeight: 900 }}>{report.mealTable.totals.Dinner}</td>
                        <td style={{ padding: '6px 8px', fontWeight: 900 }}>{report.mealTable.totals.Total}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="muted">No meal count notes found.</div>
              )}
            </div>

            <div style={{ border: '1px solid #eee', borderRadius: 12, padding: 12, background: '#fff' }}>
              <div style={{ fontWeight: 900, marginBottom: 8 }}>Finance transactions included</div>
              <div className="muted">{report.sortedFin.length} rows</div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}